import { useMemo } from 'react'
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
  Button,
  Alert,
} from '@mui/material'
import { Close, OpenInNew } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import helpArticles from '../data/helpArticles.json'

interface HelpArticle {
  id: string
  category: string
  title: string
  keywords: string[]
  content: string
  lastUpdated: string
}

interface Props {
  /** ID of the article to render (must match an `id` in helpArticles.json) */
  articleId: string
  open: boolean
  onClose: () => void
  /** Drawer width on md+; mobile is always full-width */
  width?: number
}

/**
 * Side drawer that renders a single Help Center article inline.
 * Use this anywhere we want contextual help without making the user
 * navigate away to the Help page.
 */
export default function HelpDrawer({ articleId, open, onClose, width = 540 }: Props) {
  const article = useMemo<HelpArticle | null>(
    () => (helpArticles as HelpArticle[]).find((a) => a.id === articleId) || null,
    [articleId]
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: width },
          maxWidth: '100vw',
          // Sit below the fixed AppBar (Layout.tsx Toolbar is 64px) so the
          // drawer header isn't clipped by the top nav. Matches the existing
          // `calc(100% - 64px)` content offset used elsewhere in Layout.
          top: '64px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Box sx={{ background: '#1a1a1a', color: '#fff', px: 3, pt: 2.5, pb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: 2, color: 'rgba(255,255,255,0.6)' }}
          >
            Help
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {article && (
              <Button
                component={RouterLink}
                to="/help"
                size="small"
                endIcon={<OpenInNew fontSize="small" />}
                onClick={onClose}
                sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.08)' } }}
              >
                Open in Help Center
              </Button>
            )}
            <IconButton
              onClick={onClose}
              aria-label="Close help drawer"
              sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.08)' } }}
            >
              <Close />
            </IconButton>
          </Stack>
        </Stack>
        {article && (
          <>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 500, lineHeight: 1.25 }}>
              {article.title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              Last updated {article.lastUpdated}
            </Typography>
          </>
        )}
      </Box>
      <Box sx={{ px: 3, py: 3, overflow: 'auto' }}>
        {!article ? (
          <Alert severity="warning">
            Help article "{articleId}" not found. This is a bug — please report it via the feedback
            button.
          </Alert>
        ) : (
          <Box>
            <Box
              sx={{
                mt: 0,
                '& h2': { fontSize: '1.25rem', mt: 3, mb: 1.5, fontWeight: 600 },
                '& h3': { fontSize: '1.05rem', mt: 2.5, mb: 1, fontWeight: 600 },
                '& p': { lineHeight: 1.65, mb: 1.5 },
                '& ul, & ol': { pl: 3, mb: 1.5 },
                '& li': { mb: 0.5, lineHeight: 1.55 },
                '& code': {
                  background: 'rgba(0,0,0,0.05)',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre': {
                  background: 'rgba(0,0,0,0.04)',
                  border: 1,
                  borderColor: 'divider',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.85rem',
                  '& code': { background: 'transparent', p: 0 },
                },
                '& blockquote': {
                  borderLeft: 3,
                  borderColor: 'primary.light',
                  pl: 2,
                  ml: 0,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 2,
                  fontSize: '0.92rem',
                },
                '& th, & td': {
                  border: 1,
                  borderColor: 'divider',
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                  verticalAlign: 'top',
                },
                '& th': {
                  background: 'rgba(0,0,0,0.03)',
                  fontWeight: 600,
                },
                '& strong': { fontWeight: 600 },
                '& hr': { border: 0, borderTop: 1, borderColor: 'divider', my: 3 },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}
